/**
 * Seções da integração Asaas (v9 Etapa 2, admin-only) — renderizadas num
 * único ponto da aba Financeiro de /settings/integrations (o "Gerenciar" da
 * tabela e o card-logo só alternam o expand): form de API key / ambiente /
 * ativo + bloco read-only do webhook (URL e token com copiar).
 */
import { useEffect, useState, type FormEvent } from 'react';
import { CheckCircle2, CircleAlert, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { copyText } from '@/features/vault/vault-clipboard';
import {
  asaasErrorMessage,
  isAsaasConfigured,
  useUpdateAsaasIntegration,
  type AsaasEnvironment,
  type FinanceIntegration,
} from '@/features/billing/asaas';
import { Section } from './section';

export function AsaasSections({ integration }: { integration: FinanceIntegration }) {
  const configured = isAsaasConfigured(integration);
  const [apiKey, setApiKey] = useState('');
  // `active` vem sempre true da API na visão não configurada — o switch da
  // primeira configuração já nasce ligado direto do valor do servidor.
  const [environment, setEnvironment] = useState<AsaasEnvironment>(
    integration.environment ?? 'sandbox'
  );
  const [active, setActive] = useState(integration.active);
  const [saved, setSaved] = useState(false);

  // Refetch fora do submit não deve sobrescrever o form em digitação —
  // re-sincroniza (durante o render, sem efeito) só quando os valores de que
  // o servidor é dono realmente mudam.
  const [prevServer, setPrevServer] = useState({
    environment: integration.environment,
    active: integration.active,
  });
  if (
    prevServer.environment !== integration.environment ||
    prevServer.active !== integration.active
  ) {
    setPrevServer({ environment: integration.environment, active: integration.active });
    setEnvironment(integration.environment ?? 'sandbox');
    setActive(integration.active);
  }

  const save = useUpdateAsaasIntegration();

  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(false), 3000);
    return () => clearTimeout(t);
  }, [saved]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const key = apiKey.trim();
    // Primeira configuração exige a key; depois dá para alterar só ambiente/ativo.
    if (!configured && !key) return;
    save.mutate(
      { ...(key ? { apiKey: key } : {}), environment, active },
      {
        onSuccess: () => {
          setApiKey('');
          setSaved(true);
        },
      }
    );
  };

  return (
    <div className="space-y-4">
      <Section
        title="Conexão"
        description="API key da conta Asaas ($aact_...), ambiente e ativação da integração."
      >
        <form onSubmit={submit} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="asaas-api-key">
                API key{' '}
                {configured ? null : <span className="text-destructive">*</span>}
              </Label>
              <Input
                id="asaas-api-key"
                type="password"
                autoComplete="off"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={
                  configured && integration.apiKeyMasked
                    ? `Configurada (${integration.apiKeyMasked}) — cole para trocar`
                    : '$aact_...'
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="asaas-environment">Ambiente</Label>
              <Select
                id="asaas-environment"
                value={environment}
                onChange={(e) => setEnvironment(e.target.value as AsaasEnvironment)}
              >
                <option value="sandbox">Sandbox</option>
                <option value="production">Produção</option>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="asaas-active"
              aria-label="Integração Asaas ativa"
              checked={active}
              onCheckedChange={setActive}
            />
            <Label htmlFor="asaas-active" className="cursor-pointer">
              Integração ativa
            </Label>
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="submit"
              size="sm"
              disabled={save.isPending || (!configured && apiKey.trim() === '')}
            >
              {save.isPending ? 'Salvando…' : 'Salvar'}
            </Button>
            {saved ? (
              <span className="flex items-center gap-1 text-xs text-[#52C41A]">
                <CheckCircle2 className="size-3.5" />
                Integração salva.
              </span>
            ) : null}
            {save.error ? (
              <span className="text-xs text-destructive" role="alert">
                {asaasErrorMessage(save.error)}
              </span>
            ) : null}
          </div>
        </form>
      </Section>

      {integration.webhookUrl || integration.webhookToken ? (
        <Section
          title="Webhook"
          description="Endpoint que recebe as confirmações de pagamento do Asaas."
        >
          <div className="space-y-3">
            {integration.webhookUrl ? (
              <CopyField id="asaas-webhook-url" label="URL do webhook" value={integration.webhookUrl} />
            ) : null}
            {integration.webhookToken ? (
              <CopyField
                id="asaas-webhook-token"
                label="Token de autenticação"
                value={integration.webhookToken}
              />
            ) : null}
            <p className="text-xs text-muted-foreground">
              Configure este endpoint e token no painel do Asaas — Integrações → Webhooks.
            </p>
          </div>
        </Section>
      ) : null}
    </div>
  );
}

/**
 * Campo read-only com botão copiar — usa o helper copyText (clipboard async
 * com fallback execCommand): "Copiado" por 2s no sucesso, aviso visível na
 * falha (campo continua selecionável para copiar manualmente).
 */
function CopyField({ id, label, value }: { id: string; label: string; value: string }) {
  const [feedback, setFeedback] = useState<'copied' | 'failed' | null>(null);

  useEffect(() => {
    if (feedback === null) return;
    const t = setTimeout(() => setFeedback(null), feedback === 'copied' ? 2000 : 4000);
    return () => clearTimeout(t);
  }, [feedback]);

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-2">
        <Input id={id} readOnly value={value} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          title={`Copiar ${label.toLowerCase()}`}
          aria-label={`Copiar ${label.toLowerCase()}`}
          onClick={() => {
            void copyText(value).then((ok) => setFeedback(ok ? 'copied' : 'failed'));
          }}
        >
          {feedback === 'copied' ? <Check className="text-[#52C41A]" /> : <Copy />}
        </Button>
      </div>
      {feedback === 'failed' ? (
        <p className="flex items-center gap-1 text-xs text-destructive" role="alert">
          <CircleAlert className="size-3.5 shrink-0" />
          Não foi possível copiar — selecione o valor no campo e copie manualmente.
        </p>
      ) : null}
    </div>
  );
}
